import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

import WebSocketContext from "./WebSocketContext";

const RECONNECT_MS = 1000;

function WebSocketProvider({ children }: { children: React.ReactNode }) {
  const url = process.env.REACT_APP_WS ?? "";
  const socketRef = useRef<WebSocket | null>(null);
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const intentionalCloseRef = useRef(false);
  const onOpenCallbacksRef = useRef<VoidFunction[]>([]);
  const [socket, setSocket] = useState<WebSocket | null>(null);

  const clearReconnectTimer = () => {
    if (reconnectTimerRef.current) {
      clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }
  };

  const connect = useCallback((callback: VoidFunction) => {
    onOpenCallbacksRef.current.push(callback);
    if (socketRef.current?.readyState === WebSocket.OPEN) {
      callback();
    }
  }, []);

  useEffect(() => {
    if (!url) {
      console.error("REACT_APP_WS is not set; WebSocket will not connect");
      return;
    }

    intentionalCloseRef.current = false;

    const openSocket = () => {
      clearReconnectTimer();

      const existing = socketRef.current;
      if (existing && (existing.readyState === WebSocket.OPEN || existing.readyState === WebSocket.CONNECTING)) {
        // Close previous instance without scheduling reconnect from this path
        existing.onclose = null;
        existing.close();
      }

      const ws = new WebSocket(url);
      socketRef.current = ws;
      setSocket(ws);

      ws.onopen = () => {
        console.log("Successfully Connected");
        const callbacks = [...onOpenCallbacksRef.current];
        onOpenCallbacksRef.current = [];
        callbacks.forEach((cb) => cb());
      };

      ws.onerror = (error) => {
        console.log("Socket Error: ", error);
      };

      ws.onclose = (event) => {
        console.log("Socket Closed Connection: ", event);
        if (socketRef.current === ws) {
          socketRef.current = null;
          setSocket(null);
        }
        if (intentionalCloseRef.current) {
          return;
        }
        console.log("Socket is closed. Reconnect will be attempted in 1 second.", event.reason);
        reconnectTimerRef.current = setTimeout(() => {
          openSocket();
        }, RECONNECT_MS);
      };
    };

    openSocket();

    return () => {
      intentionalCloseRef.current = true;
      clearReconnectTimer();
      if (socketRef.current) {
        socketRef.current.onclose = null;
        socketRef.current.close();
        socketRef.current = null;
      }
      setSocket(null);
    };
  }, [url]);

  const sendMessage = useCallback((scope: string, message: string) => {
    const flagMessage = JSON.stringify({ scope, ...JSON.parse(message) });
    console.log("sending msg: ", flagMessage);
    const ws = socketRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN) {
      console.warn("WebSocket is not open; message not sent");
      return;
    }
    ws.send(flagMessage);
  }, []);

  const value = useMemo(
    () => ({ socket, connect, sendMessage }),
    [socket, connect, sendMessage]
  );

  return <WebSocketContext.Provider value={value}>{children}</WebSocketContext.Provider>;
}

export default WebSocketProvider;
